import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid
} from "recharts";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Calendar, 
  Users, 
  Clock, 
  AlertTriangle,
  MapPin,
  Loader2,
  Download,
  Megaphone,
  Filter,
  UserCheck,
  XCircle,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Client, Invoice, Region, UserRole, ClientTypeRecord } from "@shared/schema";

const netRate = (inv: { eventRate?: string | null; discount?: string | null }) =>
  Math.max(0, parseFloat(inv.eventRate || "0") - parseFloat(inv.discount || "0"));

interface ReportsPageProps {
  effectiveRole?: UserRole;
}

export default function ReportsPage({ effectiveRole }: ReportsPageProps) {
  const [, navigate] = useLocation();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());

  const { data: clients = [], isLoading: clientsLoading } = useQuery<Client[]>({
    queryKey: ["/api/clients"],
  });

  const { data: invoices = [], isLoading: invoicesLoading } = useQuery<Invoice[]>({
    queryKey: ["/api/invoices"],
  });

  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ["/api/regions"],
  });

  const { data: clientTypes = [] } = useQuery<ClientTypeRecord[]>({
    queryKey: ["/api/client-types"],
  });

  // ── Wave 2 reports — server-aggregated, year-scoped ───────────────────
  const { data: leadSourceReport, isLoading: leadSourceLoading } = useQuery<{
    year: number;
    rows: Array<{ source: string | null; leadCount: number; bookingCount: number; revenue: string }>;
  }>({
    queryKey: ["/api/reports/lead-sources", { year: selectedYear }],
  });
  const { data: funnelReport, isLoading: funnelLoading } = useQuery<{
    year: number;
    overall: { leads: number; clients: number; booked: number; paid: number };
    byRep: Array<{ key: string; leads: number; clients: number; booked: number; paid: number }>;
    byRegion: Array<{ key: string; leads: number; clients: number; booked: number; paid: number }>;
    byTalent: Array<{ key: string; leads: number; clients: number; booked: number; paid: number }>;
  }>({
    queryKey: ["/api/reports/conversion-funnel", { year: selectedYear }],
  });
  const { data: profitReport, isLoading: profitLoading } = useQuery<{
    year: number;
    rows: Array<{ talentId: string | null; talentName: string | null; revenue: string; payouts: string; net: string; bookingCount: number }>;
  }>({
    queryKey: ["/api/reports/talent-profitability", { year: selectedYear }],
  });
  const { data: noShowReport, isLoading: noShowLoading } = useQuery<{
    year: number;
    overall: { totalBookings: number; noShowCount: number; noShowRate: number };
    byMonth: Array<{ month: number; totalBookings: number; noShowCount: number; noShowRate: number }>;
    byTalent: Array<{ talentId: string | null; talentName: string | null; totalBookings: number; noShowCount: number; noShowRate: number }>;
  }>({
    queryKey: ["/api/reports/no-show-rate", { year: selectedYear }],
  });
  const { data: depositReport, isLoading: depositLoading } = useQuery<{
    year: number;
    overall: { deposit: string; balance: string; total: string };
    byMonth: Array<{ month: number; deposit: string; balance: string; total: string }>;
  }>({
    queryKey: ["/api/reports/deposit-vs-balance", { year: selectedYear }],
  });

  const [funnelGroupBy, setFunnelGroupBy] = useState<"rep" | "region" | "talent">("rep");

  const monthLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  // ── Dynamic year list derived from invoice data ───────────────────────
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    invoices.forEach(inv => {
      const date = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
      if (date && !isNaN(date.getTime())) years.add(date.getFullYear());
    });
    if (!years.has(currentYear)) years.add(currentYear);
    return Array.from(years).sort((a, b) => b - a);
  }, [invoices, currentYear]);

  // ── Multi-year chart data (used when selectedYear === "all") ──────────
  const YEAR_COLORS = ["hsl(var(--primary))", "hsl(var(--primary) / 0.65)", "hsl(var(--primary) / 0.35)"];

  const multiYearData = useMemo(() => {
    if (selectedYear !== "all") return { type: "single" as const, years: [] as number[], data: [] as Record<string, unknown>[] };

    const isRevenueRow = (inv: typeof invoices[number]) =>
      inv.status !== "cancelled" && inv.status !== "draft" && inv.status !== "voided";

    const yearsWithData = availableYears.filter(y =>
      invoices.some(inv => {
        if (!isRevenueRow(inv)) return false;
        const date = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
        return date && date.getFullYear() === y;
      })
    );

    if (yearsWithData.length === 0) {
      return { type: "single" as const, years: [] as number[], data: [] as Record<string, unknown>[] };
    }

    if (yearsWithData.length <= 3) {
      // Multi-year monthly grouped view
      const data = monthLabels.map((month, idx) => {
        const row: Record<string, unknown> = { month };
        yearsWithData.forEach(year => {
          const monthInvoices = invoices.filter(inv => {
            if (!isRevenueRow(inv)) return false;
            const date = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
            return date && date.getMonth() === idx && date.getFullYear() === year;
          });
          row[`${year}_revenue`] = monthInvoices.reduce((sum, inv) => sum + netRate(inv), 0);
          row[`${year}_bookings`] = monthInvoices.length;
        });
        return row;
      });
      return { type: "multi_monthly" as const, years: yearsWithData, data };
    } else {
      // Year-by-year summary view
      const data = [...yearsWithData].reverse().map(year => {
        const yearInvoices = invoices.filter(inv => {
          if (!isRevenueRow(inv)) return false;
          const date = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
          return date && date.getFullYear() === year;
        });
        return {
          year: year.toString(),
          revenue: yearInvoices.reduce((sum, inv) => sum + netRate(inv), 0),
          bookings: yearInvoices.length,
        };
      });
      return { type: "year_summary" as const, years: yearsWithData, data };
    }
  }, [invoices, selectedYear, availableYears]);

  const depositMonthly = useMemo(() => {
    if (!depositReport) return [] as Array<{ month: string; deposit: number; balance: number }>;
    const byMonth = new Map<number, { deposit: number; balance: number }>();
    depositReport.byMonth.forEach((m) => byMonth.set(m.month, { deposit: Number(m.deposit), balance: Number(m.balance) }));
    return monthLabels.map((label, idx) => {
      const row = byMonth.get(idx + 1);
      return { month: label, deposit: row?.deposit ?? 0, balance: row?.balance ?? 0 };
    });
  }, [depositReport]);

  const noShowMonthly = useMemo(() => {
    if (!noShowReport) return [] as Array<{ month: string; rate: number; noShows: number; total: number }>;
    const byMonth = new Map<number, { rate: number; noShows: number; total: number }>();
    noShowReport.byMonth.forEach((m) => byMonth.set(m.month, { rate: m.noShowRate, noShows: m.noShowCount, total: m.totalBookings }));
    return monthLabels.map((label, idx) => {
      const row = byMonth.get(idx + 1);
      return { month: label, rate: row?.rate ?? 0, noShows: row?.noShows ?? 0, total: row?.total ?? 0 };
    });
  }, [noShowReport]);

  const activeFunnel = useMemo(() => {
    if (!funnelReport) return [] as Array<{ key: string; leads: number; clients: number; booked: number; paid: number }>;
    if (funnelGroupBy === "rep") return funnelReport.byRep.slice(0, 10);
    if (funnelGroupBy === "region") return funnelReport.byRegion.slice(0, 10);
    return funnelReport.byTalent.slice(0, 10);
  }, [funnelReport, funnelGroupBy]);

  const [groupBy, setGroupBy] = useState<"region" | "clientType">("region");

  const isLoading = clientsLoading || invoicesLoading;

  const metrics = useMemo(() => {
    const now = new Date();
    const isAll = selectedYear === "all";
    const yearNum = isAll ? 0 : parseInt(selectedYear);
    const yearStart = new Date(yearNum, 0, 1);
    const yearEnd = new Date(yearNum, 11, 31, 23, 59, 59, 999);
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    // Draft + voided + cancelled invoices are not real revenue — exclude
    // them everywhere we sum eventRate so the year cards match the
    // backend admin dashboard summary.
    const isRevenueRow = (inv: typeof invoices[number]) =>
      inv.status !== "cancelled" && inv.status !== "draft" && inv.status !== "voided";

    const yearInvoices = invoices.filter(inv => {
      if (!isRevenueRow(inv)) return false;
      if (isAll) return true;
      const date = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
      return date && date >= yearStart && date <= yearEnd;
    });

    const totalRevenue = yearInvoices.reduce((sum, inv) => 
      sum + netRate(inv), 0
    );

    const totalBookings = yearInvoices.length;

    // Outstanding: unpaid invoices scoped to the selected year (or all-time)
    const outstanding = invoices
      .filter(inv => {
        if (inv.status === "paid" || !isRevenueRow(inv)) return false;
        if (isAll) return true;
        const date = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
        return date && date >= yearStart && date <= yearEnd;
      })
      .reduce((sum, inv) => sum + netRate(inv), 0);

    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const overdueAmount = invoices
      .filter(inv => {
        if (inv.status === "paid" || !isRevenueRow(inv)) return false;
        const showDate = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
        if (!showDate || showDate >= thirtyDaysAgo) return false;
        if (isAll) return true;
        return showDate >= yearStart && showDate <= yearEnd;
      })
      .reduce((sum, inv) => sum + netRate(inv), 0);

    const thisMonthShows = yearInvoices.filter(inv => {
      const date = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
      return date && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).length;

    const avgShowRate = totalBookings > 0 ? totalRevenue / totalBookings : 0;

    const lastMonthInvoices = yearInvoices.filter(inv => {
      const date = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
      return date && date >= lastMonth && date < thisMonth;
    });
    const lastMonthRevenue = lastMonthInvoices.reduce((sum, inv) => 
      sum + netRate(inv), 0
    );

    const thisMonthInvoices = yearInvoices.filter(inv => {
      const date = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
      return date && date >= thisMonth;
    });
    const thisMonthRevenue = thisMonthInvoices.reduce((sum, inv) => 
      sum + netRate(inv), 0
    );

    const revenueDelta = lastMonthRevenue > 0 
      ? ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100 
      : 0;

    return {
      totalRevenue,
      totalBookings,
      outstanding,
      overdueAmount,
      thisMonthShows,
      avgShowRate,
      revenueDelta,
      lastMonthBookings: lastMonthInvoices.length,
      thisMonthBookings: thisMonthInvoices.length,
    };
  }, [invoices, selectedYear]);

  const agingData = useMemo(() => {
    const now = new Date();
    const isAll = selectedYear === "all";
    const yearNum = isAll ? 0 : parseInt(selectedYear);
    const yearStart = new Date(yearNum, 0, 1);
    const yearEnd = new Date(yearNum, 11, 31, 23, 59, 59, 999);

    const unpaid = invoices.filter(inv => {
      if (inv.status === "paid" || inv.status === "cancelled" || inv.status === "voided" || inv.status === "draft") return false;
      if (isAll) return true;
      const date = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
      return date && date >= yearStart && date <= yearEnd;
    });
    
    let current = 0, days30 = 0, days45 = 0, days60 = 0;
    
    unpaid.forEach(inv => {
      const showDate = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
      if (!showDate) return;
      
      const daysSinceShow = Math.floor((now.getTime() - showDate.getTime()) / (1000 * 60 * 60 * 24));
      const amount = netRate(inv);
      
      if (daysSinceShow < 30) current += amount;
      else if (daysSinceShow < 45) days30 += amount;
      else if (daysSinceShow < 60) days45 += amount;
      else days60 += amount;
    });

    return [
      { name: "Current", value: current, fill: "hsl(var(--primary))" },
      { name: "30-44 days", value: days30, fill: "hsl(var(--primary) / 0.7)" },
      { name: "45-59 days", value: days45, fill: "hsl(var(--primary) / 0.5)" },
      { name: "60+ days", value: days60, fill: "hsl(var(--primary) / 0.3)" },
    ];
  }, [invoices, selectedYear]);

  const topOutstandingClients = useMemo(() => {
    const isAll = selectedYear === "all";
    const yearNum = isAll ? 0 : parseInt(selectedYear);
    const yearStart = new Date(yearNum, 0, 1);
    const yearEnd = new Date(yearNum, 11, 31, 23, 59, 59, 999);

    const unpaidByClient = new Map<string, { name: string; amount: number }>();
    
    invoices
      .filter(inv => {
        if (inv.status === "paid" || inv.status === "cancelled" || inv.status === "voided" || inv.status === "draft") return false;
        if (isAll) return true;
        const date = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
        return date && date >= yearStart && date <= yearEnd;
      })
      .forEach(inv => {
        const name = inv.accountName || "Unknown";
        const existing = unpaidByClient.get(name) || { name, amount: 0 };
        existing.amount += netRate(inv);
        unpaidByClient.set(name, existing);
      });

    return Array.from(unpaidByClient.values())
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 8);
  }, [invoices, selectedYear]);

  const monthlyData = useMemo(() => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const year = parseInt(selectedYear);
    
    return months.map((month, idx) => {
      const monthInvoices = invoices.filter(inv => {
        const date = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
        return date && date.getMonth() === idx && date.getFullYear() === year;
      });
      
      return {
        month,
        bookings: monthInvoices.length,
        revenue: monthInvoices.reduce((sum, inv) => sum + netRate(inv), 0),
      };
    });
  }, [invoices, selectedYear]);

  const regionData = useMemo(() => {
    const isAll = selectedYear === "all";
    const yearNum = isAll ? 0 : parseInt(selectedYear);
    const yearStart = new Date(yearNum, 0, 1);
    const yearEnd = new Date(yearNum, 11, 31, 23, 59, 59, 999);

    const stats = new Map<string, { name: string; shows: number; revenue: number }>();

    clients.forEach(client => {
      let bucketName: string;
      if (groupBy === "clientType") {
        bucketName = client.clientType || "Unassigned";
      } else {
        const region = regions.find(r => r.id === client.regionId);
        bucketName = region?.name || "Unassigned";
      }

      const clientInvoices = invoices.filter(inv => {
        if (inv.clientId !== client.id) return false;
        if (inv.status === "cancelled" || inv.status === "draft" || inv.status === "voided") return false;
        if (isAll) return true;
        const date = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
        return date && date >= yearStart && date <= yearEnd;
      });
      const revenue = clientInvoices.reduce((sum, inv) => sum + netRate(inv), 0);

      const existing = stats.get(bucketName) || { name: bucketName, shows: 0, revenue: 0 };
      existing.shows += clientInvoices.length;
      existing.revenue += revenue;
      stats.set(bucketName, existing);
    });

    return Array.from(stats.values())
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);
  }, [clients, invoices, regions, groupBy, selectedYear]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-auto">
      <div className="flex items-center justify-between gap-4 p-3 sm:p-6 border-b border-border bg-card sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-semibold">Reports</h1>
          <Badge variant="secondary">Executive Overview</Badge>
        </div>
        <div className="flex items-center gap-3">
          {effectiveRole === "admin" && (
            <Button
              variant="outline"
              onClick={() => {
                window.location.href = "/api/export/csv";
              }}
              data-testid="button-export-csv"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          )}
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-[130px]" data-testid="select-report-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              {availableYears.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="p-3 sm:p-6 space-y-6 sm:space-y-8">
        <section>
          <h2 className="text-lg font-medium mb-4 text-muted-foreground">Key Metrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <MetricCard
              title="Total Revenue"
              value={`$${metrics.totalRevenue.toLocaleString()}`}
              subtitle={selectedYear}
              icon={<DollarSign className="w-5 h-5" />}
              trend={metrics.revenueDelta}
            />
            <MetricCard
              title="Total Bookings"
              value={metrics.totalBookings.toString()}
              subtitle={selectedYear}
              icon={<Calendar className="w-5 h-5" />}
            />
            <MetricCard
              title="Outstanding"
              value={`$${metrics.outstanding.toLocaleString()}`}
              subtitle="Unpaid invoices"
              icon={<Clock className="w-5 h-5" />}
              onClick={() => navigate(`/billing?paidFilter=unpaid&year=${selectedYear}`)}
            />
            <MetricCard
              title="30+ Day Overdue"
              value={`$${metrics.overdueAmount.toLocaleString()}`}
              subtitle="Needs attention"
              icon={<AlertTriangle className="w-5 h-5" />}
              isAlert={metrics.overdueAmount > 0}
              onClick={() => navigate("/billing?overdueFilter=30&paidFilter=unpaid")}
            />
            <MetricCard
              title="Shows This Month"
              value={metrics.thisMonthShows.toString()}
              subtitle="Scheduled"
              icon={<Users className="w-5 h-5" />}
              onClick={() => navigate("/master-schedule")}
            />
            <MetricCard
              title="Avg Event Rate"
              value={`$${metrics.avgShowRate.toFixed(0)}`}
              subtitle="Per booking"
              icon={<TrendingUp className="w-5 h-5" />}
            />
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section>
            <Card className="h-full">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">Revenue by Month</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    {selectedYear !== "all" ? (
                      <BarChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                        <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v as number) >= 1000 ? `${Math.round((v as number)/1000)}k` : v}`} className="text-muted-foreground" />
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} formatter={(value: number) => [`$${value.toLocaleString()}`, 'Revenue']} />
                        <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    ) : multiYearData.type === "multi_monthly" ? (
                      <BarChart data={multiYearData.data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                        <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v as number) >= 1000 ? `${Math.round((v as number)/1000)}k` : v}`} className="text-muted-foreground" />
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} formatter={(value: number, name: string) => [`$${value.toLocaleString()}`, String(name).replace("_revenue", "")]} />
                        {multiYearData.years.map((year, i) => (
                          <Bar key={year} dataKey={`${year}_revenue`} fill={YEAR_COLORS[i]} radius={[3, 3, 0, 0]} />
                        ))}
                      </BarChart>
                    ) : (
                      <BarChart data={multiYearData.data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="year" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                        <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v as number) >= 1000 ? `${Math.round((v as number)/1000)}k` : v}`} className="text-muted-foreground" />
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} formatter={(value: number) => [`$${value.toLocaleString()}`, 'Revenue']} />
                        <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </div>
                {selectedYear === "all" && multiYearData.type === "multi_monthly" && multiYearData.years.length > 0 && (
                  <div className="flex justify-center gap-4 mt-2">
                    {multiYearData.years.map((year, i) => (
                      <div key={year} className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: YEAR_COLORS[i] }} />
                        <span className="text-xs text-muted-foreground">{year}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          <section>
            <Card className="h-full">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">Bookings by Month</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    {selectedYear !== "all" ? (
                      <LineChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                        <YAxis tick={{ fontSize: 12 }} className="text-muted-foreground" />
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                        <Line type="monotone" dataKey="bookings" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ fill: 'hsl(var(--primary))', strokeWidth: 2 }} />
                      </LineChart>
                    ) : multiYearData.type === "multi_monthly" ? (
                      <LineChart data={multiYearData.data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                        <YAxis tick={{ fontSize: 12 }} className="text-muted-foreground" />
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} formatter={(value: number, name: string) => [value, String(name).replace("_bookings", "")]} />
                        {multiYearData.years.map((year, i) => (
                          <Line key={year} type="monotone" dataKey={`${year}_bookings`} stroke={YEAR_COLORS[i]} strokeWidth={2} dot={{ fill: YEAR_COLORS[i], strokeWidth: 2 }} />
                        ))}
                      </LineChart>
                    ) : (
                      <BarChart data={multiYearData.data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="year" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                        <YAxis tick={{ fontSize: 12 }} className="text-muted-foreground" />
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} formatter={(value: number) => [value, 'Bookings']} />
                        <Bar dataKey="bookings" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </div>
                {selectedYear === "all" && multiYearData.type === "multi_monthly" && multiYearData.years.length > 0 && (
                  <div className="flex justify-center gap-4 mt-2">
                    {multiYearData.years.map((year, i) => (
                      <div key={year} className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: YEAR_COLORS[i] }} />
                        <span className="text-xs text-muted-foreground">{year}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>
        </div>

        <section>
          <h2 className="text-lg font-medium mb-4 text-muted-foreground">Billing Health</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">Aging Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={agingData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, value }) => value > 0 ? `${name}: $${value.toLocaleString()}` : ''}
                        labelLine={false}
                      >
                        {agingData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value: number) => `$${value.toLocaleString()}`}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-4 mt-2">
                  {agingData.map((item) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.fill }} />
                      <span className="text-xs text-muted-foreground">{item.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">Outstanding by Client</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {topOutstandingClients.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">No outstanding invoices</p>
                  ) : (
                    topOutstandingClients.map((client, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium truncate">{client.name}</span>
                            <span className="text-sm font-semibold">${client.amount.toLocaleString()}</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-primary rounded-full transition-all"
                              style={{ 
                                width: `${Math.min(100, (client.amount / (topOutstandingClients[0]?.amount || 1)) * 100)}%` 
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-muted-foreground">Performance Breakdown</h2>
            <div className="flex items-center gap-1 rounded-md border bg-card p-1" role="tablist" aria-label="Group by">
              <Button
                size="sm"
                variant={groupBy === "region" ? "default" : "ghost"}
                onClick={() => setGroupBy("region")}
                className="h-7 px-2 text-xs"
                data-testid="button-groupby-region"
              >
                By Region
              </Button>
              <Button
                size="sm"
                variant={groupBy === "clientType" ? "default" : "ghost"}
                onClick={() => setGroupBy("clientType")}
                className="h-7 px-2 text-xs"
                data-testid="button-groupby-clienttype"
              >
                By Client Type
              </Button>
            </div>
          </div>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Revenue by {groupBy === "clientType" ? "Client Type" : "Region"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {regionData.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No regional data available</p>
              ) : (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={regionData} 
                      layout="vertical" 
                      margin={{ top: 10, right: 30, left: 100, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={90} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value: number, name: string) => {
                          if (name === 'revenue') return [`$${value.toLocaleString()}`, 'Revenue'];
                          return [value, 'Shows'];
                        }}
                      />
                      <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* ── Wave 2: Lead-source attribution ───────────────────────── */}
        <section>
          <h2 className="text-lg font-medium mb-4 text-muted-foreground">Lead Source Attribution</h2>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Megaphone className="w-4 h-4" />
                Revenue & Bookings by Lead Source
              </CardTitle>
            </CardHeader>
            <CardContent>
              {leadSourceLoading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              ) : !leadSourceReport || leadSourceReport.rows.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8" data-testid="text-leadsource-empty">No lead source data for {selectedYear}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="table-leadsource">
                    <thead>
                      <tr className="border-b border-border text-xs text-muted-foreground uppercase tracking-wide">
                        <th className="text-left py-2 px-2 font-medium">Source</th>
                        <th className="text-right py-2 px-2 font-medium">Leads</th>
                        <th className="text-right py-2 px-2 font-medium">Bookings</th>
                        <th className="text-right py-2 px-2 font-medium">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leadSourceReport.rows.map((row, idx) => (
                        <tr key={`leadsource-${idx}`} className="border-b border-border/40 hover:bg-muted/30" data-testid={`row-leadsource-${idx}`}>
                          <td className="py-2 px-2">{row.source ?? <span className="italic text-muted-foreground">Unattributed</span>}</td>
                          <td className="py-2 px-2 text-right tabular-nums">{row.leadCount.toLocaleString()}</td>
                          <td className="py-2 px-2 text-right tabular-nums">{row.bookingCount.toLocaleString()}</td>
                          <td className="py-2 px-2 text-right tabular-nums font-medium">${Number(row.revenue).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* ── Wave 2: Conversion funnel ─────────────────────────────── */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-muted-foreground">Conversion Funnel</h2>
            <div className="flex items-center gap-1 rounded-md border bg-card p-1" role="tablist" aria-label="Funnel group by">
              <Button size="sm" variant={funnelGroupBy === "rep" ? "default" : "ghost"} onClick={() => setFunnelGroupBy("rep")} className="h-7 px-2 text-xs" data-testid="button-funnel-rep">By Rep</Button>
              <Button size="sm" variant={funnelGroupBy === "region" ? "default" : "ghost"} onClick={() => setFunnelGroupBy("region")} className="h-7 px-2 text-xs" data-testid="button-funnel-region">By Region</Button>
              <Button size="sm" variant={funnelGroupBy === "talent" ? "default" : "ghost"} onClick={() => setFunnelGroupBy("talent")} className="h-7 px-2 text-xs" data-testid="button-funnel-talent">By Talent</Button>
            </div>
          </div>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Leads → Clients → Booked → Paid
              </CardTitle>
            </CardHeader>
            <CardContent>
              {funnelLoading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              ) : !funnelReport ? (
                <p className="text-sm text-muted-foreground text-center py-8">No funnel data</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <FunnelStage label="Leads" value={funnelReport.overall.leads} base={funnelReport.overall.leads} />
                    <FunnelStage label="Clients" value={funnelReport.overall.clients} base={funnelReport.overall.leads} />
                    <FunnelStage label="Booked" value={funnelReport.overall.booked} base={funnelReport.overall.leads} />
                    <FunnelStage label="Paid" value={funnelReport.overall.paid} base={funnelReport.overall.leads} />
                  </div>
                  {activeFunnel.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No data for this grouping</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="table-funnel">
                        <thead>
                          <tr className="border-b border-border text-xs text-muted-foreground uppercase tracking-wide">
                            <th className="text-left py-2 px-2 font-medium">{funnelGroupBy === "rep" ? "Rep" : funnelGroupBy === "region" ? "Region" : "Talent"}</th>
                            <th className="text-right py-2 px-2 font-medium">Leads</th>
                            <th className="text-right py-2 px-2 font-medium">Clients</th>
                            <th className="text-right py-2 px-2 font-medium">Booked</th>
                            <th className="text-right py-2 px-2 font-medium">Paid</th>
                            <th className="text-right py-2 px-2 font-medium">Lead → Paid %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeFunnel.map((row, idx) => {
                            const pct = row.leads > 0 ? ((row.paid / row.leads) * 100).toFixed(1) : "0.0";
                            return (
                              <tr key={`funnel-${idx}`} className="border-b border-border/40 hover:bg-muted/30" data-testid={`row-funnel-${idx}`}>
                                <td className="py-2 px-2 truncate max-w-[200px]">{row.key}</td>
                                <td className="py-2 px-2 text-right tabular-nums">{row.leads}</td>
                                <td className="py-2 px-2 text-right tabular-nums">{row.clients}</td>
                                <td className="py-2 px-2 text-right tabular-nums">{row.booked}</td>
                                <td className="py-2 px-2 text-right tabular-nums">{row.paid}</td>
                                <td className="py-2 px-2 text-right tabular-nums font-medium">{pct}%</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </section>

        {/* ── Wave 2: Talent profitability ────────────────────── */}
        <section>
          <h2 className="text-lg font-medium mb-4 text-muted-foreground">Talent Profitability (Top 10)</h2>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <UserCheck className="w-4 h-4" />
                Revenue minus Payouts = Net Contribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              {profitLoading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              ) : !profitReport || profitReport.rows.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8" data-testid="text-profit-empty">No talent data for {selectedYear}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="table-profitability">
                    <thead>
                      <tr className="border-b border-border text-xs text-muted-foreground uppercase tracking-wide">
                        <th className="text-left py-2 px-2 font-medium">Talent</th>
                        <th className="text-right py-2 px-2 font-medium">Bookings</th>
                        <th className="text-right py-2 px-2 font-medium">Revenue</th>
                        <th className="text-right py-2 px-2 font-medium">Payouts</th>
                        <th className="text-right py-2 px-2 font-medium">Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profitReport.rows.map((row, idx) => {
                        const net = Number(row.net);
                        return (
                          <tr key={`profit-${idx}`} className="border-b border-border/40 hover:bg-muted/30" data-testid={`row-profit-${idx}`}>
                            <td className="py-2 px-2">{row.talentName ?? <span className="italic text-muted-foreground">Unassigned</span>}</td>
                            <td className="py-2 px-2 text-right tabular-nums">{row.bookingCount}</td>
                            <td className="py-2 px-2 text-right tabular-nums">${Number(row.revenue).toLocaleString()}</td>
                            <td className="py-2 px-2 text-right tabular-nums">${Number(row.payouts).toLocaleString()}</td>
                            <td className={`py-2 px-2 text-right tabular-nums font-semibold ${net < 0 ? "text-destructive" : ""}`}>${net.toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* ── Wave 2: No-show rate ─────────────────────────────────── */}
        <section>
          <h2 className="text-lg font-medium mb-4 text-muted-foreground">No-Event Rate</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Monthly No-Event Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                {noShowLoading ? (
                  <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                ) : !noShowReport ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No data</p>
                ) : (
                  <>
                    <div className="mb-4 text-sm text-muted-foreground">
                      Overall: <span className="font-semibold text-foreground" data-testid="text-noshow-overall">{noShowReport.overall.noShowRate.toFixed(1)}%</span> ({noShowReport.overall.noShowCount} of {noShowReport.overall.totalBookings})
                    </div>
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={noShowMonthly} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="month" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} className="text-muted-foreground" />
                          <Tooltip
                            contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                            formatter={(value: number, _name: string, p: any) => [`${value.toFixed(1)}% (${p.payload.noShows} of ${p.payload.total})`, 'No-show rate']}
                          />
                          <Bar dataKey="rate" fill="hsl(var(--destructive))" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">By Talent</CardTitle>
              </CardHeader>
              <CardContent>
                {noShowLoading ? (
                  <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                ) : !noShowReport || noShowReport.byTalent.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No data</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="table-noshow-talent">
                      <thead>
                        <tr className="border-b border-border text-xs text-muted-foreground uppercase tracking-wide">
                          <th className="text-left py-2 px-2 font-medium">Talent</th>
                          <th className="text-right py-2 px-2 font-medium">Bookings</th>
                          <th className="text-right py-2 px-2 font-medium">No-shows</th>
                          <th className="text-right py-2 px-2 font-medium">Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {noShowReport.byTalent.slice(0, 10).map((row, idx) => (
                          <tr key={`noshow-${idx}`} className="border-b border-border/40 hover:bg-muted/30" data-testid={`row-noshow-${idx}`}>
                            <td className="py-2 px-2">{row.talentName ?? <span className="italic text-muted-foreground">Unassigned</span>}</td>
                            <td className="py-2 px-2 text-right tabular-nums">{row.totalBookings}</td>
                            <td className="py-2 px-2 text-right tabular-nums">{row.noShowCount}</td>
                            <td className={`py-2 px-2 text-right tabular-nums font-medium ${row.noShowRate > 5 ? "text-destructive" : ""}`}>{row.noShowRate.toFixed(1)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ── Wave 2: Deposit vs balance collection ────────────────── */}
        <section>
          <h2 className="text-lg font-medium mb-4 text-muted-foreground">Deposit vs Balance Collection</h2>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Wallet className="w-4 h-4" />
                Monthly Collection — {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {depositLoading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              ) : !depositReport ? (
                <p className="text-sm text-muted-foreground text-center py-8">No collection data</p>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="rounded-md border bg-card p-3">
                      <p className="text-xs text-muted-foreground">Deposits</p>
                      <p className="text-xl font-bold tabular-nums" data-testid="text-deposit-total">${Number(depositReport.overall.deposit).toLocaleString()}</p>
                    </div>
                    <div className="rounded-md border bg-card p-3">
                      <p className="text-xs text-muted-foreground">Balance</p>
                      <p className="text-xl font-bold tabular-nums" data-testid="text-balance-total">${Number(depositReport.overall.balance).toLocaleString()}</p>
                    </div>
                    <div className="rounded-md border bg-card p-3">
                      <p className="text-xs text-muted-foreground">Total Collected</p>
                      <p className="text-xl font-bold tabular-nums" data-testid="text-collected-total">${Number(depositReport.overall.total).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={depositMonthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                        <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} className="text-muted-foreground" />
                        <Tooltip
                          contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                          formatter={(value: number, name: string) => [`$${value.toLocaleString()}`, name === "deposit" ? "Deposit" : "Balance"]}
                        />
                        <Bar dataKey="deposit" stackId="a" fill="hsl(var(--primary))" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="balance" stackId="a" fill="hsl(var(--primary) / 0.5)" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}

interface FunnelStageProps {
  label: string;
  value: number;
  base: number;
}

function FunnelStage({ label, value, base }: FunnelStageProps) {
  const pct = base > 0 ? Math.min(100, (value / base) * 100) : 0;
  return (
    <div className="rounded-md border bg-card p-3" data-testid={`funnel-stage-${label.toLowerCase()}`}>
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold tabular-nums">{value.toLocaleString()}</p>
      <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-muted-foreground mt-1">{pct.toFixed(0)}% of leads</p>
    </div>
  );
}

interface MetricCardProps {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  trend?: number;
  isAlert?: boolean;
  onClick?: () => void;
}

function MetricCard({ title, value, subtitle, icon, trend, isAlert, onClick }: MetricCardProps) {
  return (
    <Card className="relative overflow-hidden hover-elevate cursor-pointer" onClick={onClick}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className={`p-2 rounded-lg ${isAlert ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'}`}>
            {icon}
          </div>
          {trend !== undefined && trend !== 0 && (
            <div className={`flex items-center gap-1 text-xs font-medium ${trend > 0 ? 'text-primary' : 'text-destructive'}`}>
              {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {Math.abs(trend).toFixed(0)}%
            </div>
          )}
        </div>
        <div className="space-y-1">
          <p className="text-2xl font-bold tracking-tight">{value}</p>
          <p className="text-xs text-muted-foreground">{title}</p>
          <p className="text-xs text-muted-foreground/70">{subtitle}</p>
        </div>
      </CardContent>
    </Card>
  );
}
